/**
 * Report periods wider than one month (rd15 R1): which months a range covers,
 * which months a comparison covers, and whether a custom pair of dates can be
 * read at all.
 *
 * Pure month arithmetic: no DOM, no reads, no words a person reads. The
 * Reports page composes a range from the monthly statements it already knows
 * how to read, so a range total is the sum of its months and no new route is
 * needed. Nothing here parses or prints an API token.
 */

import { shiftPeriod } from "../ui/period-control.js";

/** The shape of the chosen period, not the words on the dropdown. */
export type ScopePreset = "month" | "quarter" | "year" | "custom";

/** Which period the comparison column holds. */
export type CompareChoice = "none" | "previous" | "same-last-year";

/**
 * The most months one custom range may compose. Each month is one read of the
 * Worker's monthly statement, so an unbounded range is a silent request storm:
 * two years is the longest span a person reads on one page.
 */
export const MAX_CUSTOM_MONTHS = 24;

const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The last day of a `YYYY-MM` month, as `YYYY-MM-DD`. */
export function monthEndOf(period: string): string {
  const [ys, ms] = period.split("-");
  const year = Number(ys);
  const month = Number(ms);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return `${period}-01`;
  }
  const date = new Date(Date.UTC(year, month, 0));
  return `${String(date.getUTCFullYear())}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

/** Every month from `start` to `end` inclusive, oldest first. Invalid input is `[]`. */
export function monthsBetween(start: string, end: string): string[] {
  if (!PERIOD_RE.test(start) || !PERIOD_RE.test(end) || start > end) return [];
  const months: string[] = [];
  let cursor = start;
  while (cursor <= end) {
    months.push(cursor);
    const next = shiftPeriod(cursor, 1);
    // shiftPeriod returns its own input when it cannot read it; stop rather
    // than loop forever on a shape this file did not expect.
    if (next === cursor) break;
    cursor = next;
  }
  return months;
}

export type CustomMonths =
  | { readonly ok: true; readonly months: string[] }
  /**
   * `not-whole-months`: something in the range is not a whole calendar month,
   * which the monthly statements cannot compose. `too-many`: the span is
   * longer than one page may read. `backwards`: the end is before the start.
   * `future`: the range ends after the clock month, where the books have no
   * days yet — reading it would list months as "not available yet" and call
   * the current month's own future a missing statement. The page owns the
   * words for each.
   */
  | {
      readonly ok: false;
      readonly why: "not-whole-months" | "too-many" | "backwards" | "future";
    };

/**
 * The months a pair of dates covers when it can be composed from monthly
 * statements: the start must be a month's first day, and the end must be a
 * month's last day (or any day inside the clock month, which is month to
 * date). A pair that is not whole months is refused rather than rounded: a
 * range that quietly became June to August when June 5 was asked for would be
 * a wrong number with a right-looking strip beside it.
 */
export function customRangeMonths(
  start: string,
  end: string,
  clockMonth: string,
): CustomMonths {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return { ok: false, why: "not-whole-months" };
  }
  if (end < start) return { ok: false, why: "backwards" };
  if (!start.endsWith("-01")) return { ok: false, why: "not-whole-months" };
  const endMonth = end.slice(0, 7);
  if (!PERIOD_RE.test(endMonth)) return { ok: false, why: "not-whole-months" };
  // A range that runs past the clock month asks the books for days that have
  // not happened: every month after this one would be listed as "not
  // available yet". Refuse it and let the page say which side is wrong.
  if (endMonth > clockMonth || start.slice(0, 7) > clockMonth) {
    return { ok: false, why: "future" };
  }
  const wholeMonth = end === monthEndOf(endMonth) || endMonth === clockMonth;
  if (!wholeMonth) return { ok: false, why: "not-whole-months" };
  const months = monthsBetween(start.slice(0, 7), endMonth);
  if (months.length === 0) return { ok: false, why: "not-whole-months" };
  if (months.length > MAX_CUSTOM_MONTHS) return { ok: false, why: "too-many" };
  return { ok: true, months };
}

/**
 * Which shape the chosen period has, for the comparison's arithmetic. The
 * dropdown's value ("this quarter") is not enough: the same two months mean a
 * quarter, a custom range or the start of a year, and "the period before"
 * means something different in each.
 */
export type CompareBasis = "month" | "quarter" | "year" | "custom";

/**
 * The months the comparison column reads.
 *
 * "The period before" is honest per shape:
 * - a single month and a custom range compare with the same number of months
 *   immediately before them (a one-month view with the month before it);
 * - a quarter compares with the previous quarter, taking the same elapsed
 *   months: each month of the quarter on screen moves back exactly one quarter
 *   (three months), so a quarter whose books begin part-way through — November
 *   alone, say — compares with the same elapsed month of the quarter before
 *   (August), not with the month that happens to sit three months before the
 *   quarter's opening month;
 * - a year to date compares with the same months last year, because the period
 *   before January is not a year to date.
 * "The same period last year" is the same months a year earlier, whatever the
 * shape. Both are calendar arithmetic on the chosen months; the difference
 * printed on screen is computed from the two compositions, never asked for
 * again.
 */
export function compareMonths(
  months: readonly string[],
  choice: CompareChoice,
  basis: CompareBasis = "custom",
): string[] {
  const first = months[0];
  if (choice === "none" || first === undefined) return [];
  if (choice === "same-last-year" || basis === "year") {
    return months.map((month) => shiftPeriod(month, -12));
  }
  if (basis === "quarter") {
    // One quarter earlier, month for month. Moving the FIRST month back three
    // months keeps its position inside its quarter, so the last month of the
    // previous quarter is never compared with the first month of a quarter the
    // reader is not looking at.
    let cursor = shiftPeriod(first, -3);
    const out: string[] = [];
    for (let index = 0; index < months.length; index += 1) {
      out.push(cursor);
      cursor = shiftPeriod(cursor, 1);
    }
    return out;
  }
  return monthsBetween(shiftPeriod(first, -months.length), shiftPeriod(first, -1));
}

/** True when two month lists are the same months in the same order. */
export function sameMonths(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return left.length === right.length && left.every((month, index) => month === right[index]);
}
